/**
 * Avatar — Radix-backed avatar with image + fallback initials (Claude Design
 * `.avatar`): a 30px bordered circle (sm 22px · lg 38px) with bold initials.
 */
import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type ReactElement,
} from 'react';
import * as AvatarPrimitive from '@radix-ui/react-avatar';

import { cn } from './cn.js';

export type AvatarSize = 'sm' | 'md' | 'lg';

const sizeClasses: Record<AvatarSize, string> = {
  sm: 'h-[22px] w-[22px] text-[9.5px]',
  md: 'h-[30px] w-[30px] text-[11.5px]',
  lg: 'h-[38px] w-[38px] text-sm',
};

export const AvatarRoot = forwardRef<
  ElementRef<typeof AvatarPrimitive.Root>,
  ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn(
      'relative inline-flex h-[30px] w-[30px] shrink-0 select-none items-center justify-center overflow-hidden rounded-full border border-border-strong bg-surface-2 text-[11.5px]',
      className,
    )}
    {...props}
  />
));
AvatarRoot.displayName = 'AvatarRoot';

export const AvatarImage = forwardRef<
  ElementRef<typeof AvatarPrimitive.Image>,
  ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={cn('h-full w-full object-cover', className)}
    {...props}
  />
));
AvatarImage.displayName = 'AvatarImage';

export const AvatarFallback = forwardRef<
  ElementRef<typeof AvatarPrimitive.Fallback>,
  ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      'flex h-full w-full items-center justify-center font-bold text-white',
      className,
    )}
    {...props}
  />
));
AvatarFallback.displayName = 'AvatarFallback';

export interface AvatarProps {
  src?: string;
  alt?: string;
  /** Text shown when the image is absent/loading (e.g. initials). */
  fallback: string;
  /** Avatar diameter: sm (22px) · md (30px, default) · lg (38px). */
  size?: AvatarSize;
  /** Background color for the initials fallback (design avatars are tinted). */
  color?: string;
  className?: string;
}

/** Convenience all-in-one avatar. */
export function Avatar({
  src,
  alt,
  fallback,
  size = 'md',
  color,
  className,
}: AvatarProps): ReactElement {
  return (
    <AvatarRoot className={cn(sizeClasses[size], className)}>
      {src ? <AvatarImage src={src} alt={alt ?? ''} /> : null}
      <AvatarFallback style={{ background: color ?? '#6c6f7a' }}>
        {fallback}
      </AvatarFallback>
    </AvatarRoot>
  );
}
